import { animalsTypeCounter, formattedDate } from 'helpers/utils';
import { TFetchConverter } from '../types/converter.types';
import {
  TRoommateAnimalDB,
  TRoommateAnimalUI,
  TRoommateResidentDB,
  TRoommateResidentUI,
  TRoommatesDB,
  TRoommatesUI,
} from '../types/roommates.types';
import {
  aggressiveBehaviorConverter,
  animalStatusConverter,
  animalTypeConverter,
  animalVetRecordConvertor,
  animalWeightConverter,
  applicationSADocPacketConvertor,
  dataOverviewConvertor,
  dnaInfoConverter,
} from './animals.converter';
import { verificationStatusConverter, genderEnumConvertor, reproductiveConvertor } from './general.converters';
import { animalStatusDetailsConverter, residenceStatusesConverter } from './residence.converters';
import { getHPDocPacketsConvertor } from './docPacket.convertors';

export const roommateAnimalConverter: TFetchConverter<TRoommateAnimalUI, TRoommateAnimalDB> = {
  fromDb: (data) => {
    return {
      id: data.id,
      name: data.name,
      profilePicture: data.profilePicture,
      status: animalStatusConverter.fromDb(data.status),
      splitStatus: animalStatusDetailsConverter.fromDb({ statusDetails: data.statusDetails, residencyStatus: null }),
      type: animalTypeConverter.fromDb(data.type),
      birthDate: data.birthDate ? formattedDate(`${data.birthDate}Z`, 'MM/DD/YYYY') : null,
      deleted: data.deleted,
      dnaFeatureEnabled: data.dnaFeatureEnabled,
      dnaInfo: data.dnaInfo ? dnaInfoConverter.fromDb(data.dnaInfo) : null,
      breed: data.breed,
      species: data.species,
      primaryColor: data.primaryColor,
      weight: data.weight ? animalWeightConverter.fromDb(data.weight) : null,
      aggressiveBehavior: data.aggressiveBehavior && aggressiveBehaviorConverter.fromDb(data.aggressiveBehavior),
      isHouseTrained: data.isHouseTrained,
      reproduction: data.isReproductive !== null ? reproductiveConvertor.fromDb(data.gender) : null,
      isToPetConverted: data.isToPetConverted,
      photoGallery: data.photoGallery,
      policyContent: data.policyContent,
      secondaryColor1: data.secondaryColor1,
      secondaryColor2: data.secondaryColor2,
      saDocuments: (data.saDocuments || []).map((item) => {
        item.modifiable = false; // hardcoded for edit disable
        return applicationSADocPacketConvertor.fromDb(item);
      }),
      hpDocPackets: (data.hpDocPackets || []).map((packet) => {
        packet.modifiable = false; // hardcoded for edit disable
        return {
          ...getHPDocPacketsConvertor.fromDb(packet),
        };
      }),
      verificationStatus: verificationStatusConverter.fromDb(data.verificationStatus),
      vetRecords: (data.vetRecords || []).map((item) => {
        item.locked = true; // hardcoded for edit disable
        return animalVetRecordConvertor.fromDb(item);
      }),
      gender: genderEnumConvertor.fromDb(data.gender),
      dataOverview: dataOverviewConvertor.fromDb(data.dataOverview),
      isRequiredBecauseOfDisability: data.isRequiredBecauseOfDisability,
      trainedFor: data.trainedFor,
      microchip: data.microchip,
      animalApplicationId: data.animalApplicationId,
      animalResidencyId: data.animalResidencyId,
      animalResidencyStatusChangeInfo: data.animalResidencyStatusChangeInfo,
    };
  },
};

/**
 * The UI shows an unanswered animal indication as "N/A" rather than as a "No".
 */
const toYesNoOrNA = (value: boolean | null): string => {
  if (value === null) {
    return 'N/A';
  }
  return value ? 'Yes' : 'No';
};

export const roommatesResidentConverter: TFetchConverter<TRoommateResidentUI, TRoommateResidentDB> = {
  fromDb: (data) => {
    return {
      email: data.email,
      hasAnimal: toYesNoOrNA(data.hasAnimal),
      name: data.name,
      phone: data.phone,
      profilePicture: data.profilePicture,
      residentId: data.residentId,
      roommateHasAnimal: toYesNoOrNA(data.roommateHasAnimal),
      status: residenceStatusesConverter.fromDb(data.status),
      animals: data.animals.map((animal) => roommateAnimalConverter.fromDb(animal)),
      animalsCount: animalsTypeCounter(data.animals),
      tenantResidencyId: data.tenantResidencyId,
      residencyStatusChangeInfo: data.residencyStatusChangeInfo,
    };
  },
};

export const roommatesConverter: TFetchConverter<TRoommatesUI, TRoommatesDB> = {
  fromDb: (data) => ({
    leaseToDate: data.leaseToDate ? formattedDate(data.leaseToDate) : '-',
    moveInDate: data.moveInDate ? formattedDate(data.moveInDate) : '-',
    propertyName: data.propertyName,
    unitNumber: data.unitNumber,
    shouldTenantSignAddendum: data.shouldTenantSignAddendum,
    resident: roommatesResidentConverter.fromDb(data.resident),
    roommates: data.roommates.map((roommate) => roommatesResidentConverter.fromDb(roommate)),
    residencyStatusChangeInfo: data.residencyStatusChangeInfo,
  }),
};
